/**
 * SVG图标管理工具
 * 使用 require.context 预先注册所有图标，实现按需加载
 * 同时支持预加载关键图标以提升用户体验
 */

class IconManager {
  constructor() {
    // 存储所有可用的图标模块
    this.iconModules = {}

    // 存储已加载的图标
    this.loadedIcons = new Set()

    // 关键图标列表（如Tabbar图标）
    this.criticalIcons = ['home', 'category', 'cart', 'my']

    // 初始化图标注册表
    this.registerAllIcons()

    // 预加载关键图标
    this.preloadCriticalIcons()
  }

  /**
   * 使用 require.context 注册所有图标
   * 这样 webpack 可以在编译时知道所有可能的图标路径
   */
  registerAllIcons() {
    try {
      // 使用 require.context 获取所有 SVG 文件
      const requireContext = require.context(
        '@/assets/icons',
        false,
        /\.svg$/
      )

      // 遍历所有图标文件并注册
      requireContext.keys().forEach(fileName => {
        // 提取图标名称（去掉 ./ 和 .svg）
        const iconName = fileName
          .replace(/^\.\//, '')
          .replace(/\.svg$/, '')

        // 存储图标的加载函数（懒加载）
        this.iconModules[iconName] = () => {
          return new Promise((resolve, reject) => {
            try {
              const module = requireContext(fileName)
              resolve(module)
            } catch (err) {
              reject(err)
            }
          })
        }
      })

      console.log(
        `[IconManager] Registered ${
          Object.keys(this.iconModules).length
        } icons`
      )
    } catch (error) {
      console.error(
        '[IconManager] Failed to register icons:',
        error
      )
    }
  }

  /**
   * 动态加载SVG图标
   * @param {string} iconName 图标名称
   * @returns {Promise} 图标导入Promise
   */
  async loadIcon(iconName) {
    // 参数验证
    if (!iconName || typeof iconName !== 'string') {
      return Promise.reject(new Error('Invalid icon name'))
    }

    // 如果图标已加载，直接返回
    if (this.loadedIcons.has(iconName)) {
      return Promise.resolve()
    }

    // 检查图标是否已注册
    if (!this.iconModules[iconName]) {
      const error = new Error(
        `Icon "${iconName}" not found. Available icons: ${Object.keys(
          this.iconModules
        ).join(', ')}`
      )
      console.warn(error.message)
      return Promise.reject(error)
    }

    try {
      // 加载图标
      const result = await this.iconModules[iconName]()

      // 标记图标已加载
      this.loadedIcons.add(iconName)

      return result
    } catch (error) {
      console.warn(
        `Failed to load icon: ${iconName}`,
        error
      )
      throw error
    }
  }

  /**
   * 预加载关键图标（如Tabbar图标）
   */
  preloadCriticalIcons() {
    // 使用Promise.allSettled确保即使某个图标加载失败也不会影响其他图标
    return Promise.allSettled(
      this.criticalIcons.map(iconName =>
        this.loadIcon(iconName)
      )
    ).then(results => {
      // 记录加载结果
      const successful = results.filter(
        result => result.status === 'fulfilled'
      ).length
      const failed = results.filter(
        result => result.status === 'rejected'
      ).length

      if (failed > 0) {
        console.warn(
          `[IconManager] Failed to preload ${failed} out of ${this.criticalIcons.length} critical icons`
        )
      } else {
        console.log(
          `[IconManager] Successfully preloaded all ${successful} critical icons`
        )
      }

      return results
    })
  }

  /**
   * 批量预加载图标
   * @param {Array<string>} iconNames 图标名称数组
   */
  preloadIcons(iconNames) {
    if (!Array.isArray(iconNames)) {
      return Promise.reject(
        new Error('Icon names must be an array')
      )
    }

    return Promise.allSettled(
      iconNames.map(iconName => this.loadIcon(iconName))
    )
  }

  /**
   * 检查图标是否已加载
   * @param {string} iconName 图标名称
   * @returns {boolean} 是否已加载
   */
  isIconLoaded(iconName) {
    return this.loadedIcons.has(iconName)
  }

  /**
   * 获取已加载的图标列表
   * @returns {Array<string>} 已加载的图标名称数组
   */
  getLoadedIcons() {
    return Array.from(this.loadedIcons)
  }
}

// 创建单例实例
const iconManager = new IconManager()

// 导出实例和类
export default iconManager
export { IconManager }
